import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  MICROSOFT_SCOPES,
  MicrosoftIntegrationConfig
} from '@dadohr/shared/domainTypes/integrations/microsoft';
import {
  Button,
  Dialog,
  DialogContent,
  styled,
  TextField,
  Typography
} from '@material-ui/core';
import { Alert, Skeleton } from '@material-ui/lab';
import { useEffect, useRef, useState } from 'react';
import { Functionality } from '../..';
import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { DialogActionsWithSlots } from '../../../../../../components/DialogActionsWithSlots';
import { DialogTitleWithTypography } from '../../../../../../components/DialogTitleWithTypography';
import { InfoBox } from '../../../../../../components/InfoBox';
import { LinkExternal } from '../../../../../../components/LinkExternal';
import ENVS from '../../../../../../env.json';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { useFirebaseFunction } from '../../../../../../services/firebaseFunctions';
import { PageOAuthRedirectProps } from '../../../../../OAuth/pages/Redirect';
import { IntegrationData } from '../../components/IntegrationData';
import {
  IntegrationConfigurationButton,
  IntegrationSetupDialogProperties,
  masked
} from '../common';
import { OAuthSetupDialog } from '../oauth';
import { removeMicrosoftConfig, useMicrosoftConfig } from './service';

const NAME = 'Microsoft';
const OAUTH = ENVS.microsoft.oauth;

export const MICROSOFT_OAUTH_REDIRECT_PROPS: PageOAuthRedirectProps = {
  integrationName: NAME,

  // TODO: FOR MIGRATION
  internalIntegrationName: 'microsoft',
  cloudFn: 'microsoft_exchangeCodeAndStoreCredentials'
};

interface MicrosoftSetupDialogProps extends IntegrationSetupDialogProperties {
  orgId: string;
  config: Doc<MicrosoftIntegrationConfig> | null;
}

const Content = styled(DialogContent)((p) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: p.theme.spacing(2)
}));

const InitialSetupDialog = ({
  open,
  orgId,
  onClose
}: {
  open: boolean;
  orgId: string;
  onClose: () => void;
}) => {
  const [tenant, setTenant] = useState<string | null>(null);
  const { t } = useUnsafeTranslation();

  return (
    <OAuthSetupDialog
      orgId={orgId}
      open={open}
      onClose={onClose}
      name={NAME}
      oauthUrl={`https://login.microsoftonline.com/${tenant}/oauth2/v2.0/authorize`}
      clientId={OAUTH.clientId}
      redirectUri={OAUTH.redirectUri}
      otherStateParams={{
        tenantId: tenant!
      }}
      otherParams={{
        scope: MICROSOFT_SCOPES.join(' '),
        access_type: 'offline'
      }}
      buttonDisabled={!tenant || !/^[0-9a-z]+(-[0-9a-z]+){4}$/i.test(tenant)}
    >
      <Alert severity="info" icon={false}>
        {t('org.integrations.microsoft.setupInfo.p1')}
      </Alert>
      <Typography component="p" variant="body1">
        {t('org.integrations.microsoft.setupInfo.p2')}
      </Typography>

      <Typography component="p" variant="caption">
        {t('org.integrations.microsoft.setupInfo.p3')}{' '}
        <LinkExternal
          color="primary"
          href="https://learn.microsoft.com/en-us/azure/active-directory/manage-apps/add-application-portal"
        >
          {t('org.integrations.microsoft.setupInfo.p3-link')}
        </LinkExternal>
      </Typography>

      <TextField
        label={t('Tenant ID')}
        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
        value={tenant}
        onChange={(event) => setTenant(event.target.value.trim())}
        helperText={
          <LinkExternal
            color="primary"
            href="https://learn.microsoft.com/en-us/azure/active-directory/fundamentals/active-directory-how-to-find-tenant#find-tenant-id-through-the-azure-portal"
          >
            {t('org.integrations.microsoft.setupInfo.tenantId')}
          </LinkExternal>
        }
        fullWidth
      />

      <InfoBox>{t('org.integrations.microsoft.setupInfo.p4')}</InfoBox>
    </OAuthSetupDialog>
  );
};

const ExistingSetupDialog = ({
  config,
  open,
  onClose
}: {
  config: Doc<MicrosoftIntegrationConfig>;
  open: boolean;
  onClose: () => void;
}) => {
  const { t } = useUnsafeTranslation();
  const [integrationAccountInfo] = useFirebaseFunction<{
    mail: string;
  }>('integrations_microsoft-getIntegrationAccountInfo', {
    orgId: config.data.orgId
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {NAME} {t('org.integrations.integration')}
      </DialogTitleWithTypography>
      <Content>
        <IntegrationData>
          <strong>{t('Email')}</strong>
          <div>
            {integrationAccountInfo ? (
              integrationAccountInfo.mail
            ) : (
              <Skeleton />
            )}
          </div>
          <strong>{t('org.integrations.common.scopes')}</strong>
          <div>{config.data.scopes.join('\n')}</div>
          <strong>{t('org.integrations.common.accessToken')}</strong>
          <div>{masked(config.data.accessToken)}</div>
          <strong>{t('org.integrations.common.refreshToken')}</strong>
        </IntegrationData>
      </Content>
      <DialogActionsWithSlots
        left={
          <ButtonWithPromise
            variant="contained"
            color="secondary"
            onClick={() => removeMicrosoftConfig(config.data.orgId)}
            pending={`${t('Removing')}...`}
          >
            {t('Remove')}
          </ButtonWithPromise>
        }
        right={
          <Button color="primary" onClick={onClose}>
            {t('Close')}
          </Button>
        }
      />
    </Dialog>
  );
};

const SetupDialog = ({
  config,
  onClose,
  onSuccessfulIntegration,
  orgId,
  open
}: MicrosoftSetupDialogProps) => {
  const setup = !!config?.data.serializedTokenCache;
  const previousConfig = useRef<Doc<MicrosoftIntegrationConfig> | null>();

  // This is a bit of a hack to detect when the config active status changes so we can show the modal dialog
  // here even if the integration is completed on another browser tab.
  useEffect(() => {
    if (
      previousConfig.current?.data &&
      config?.data.active &&
      !previousConfig.current?.data.active
    ) {
      onSuccessfulIntegration();
    }
    previousConfig.current = config;
  }, [config, onSuccessfulIntegration]);

  return (
    <>
      {setup ? (
        <ExistingSetupDialog config={config} open={open} onClose={onClose} />
      ) : (
        <InitialSetupDialog orgId={orgId} open={open} onClose={onClose} />
      )}
    </>
  );
};

export const MicrosoftSettingsButton = ({
  functionalities,
  interactive
}: {
  functionalities: Functionality[];
  interactive: boolean;
}) => {
  const { id: userId, org } = useCurrentUser();
  const [config] = useMicrosoftConfig(org.id, userId);
  const cfg = config || null;

  return (
    <IntegrationConfigurationButton
      functionalities={functionalities}
      name={NAME}
      logoUrl="/images/integrations-logos/microsoft/microsoft-logo.png"
      active={config?.data.active ?? false}
      interactive={interactive}
      renderSetupDialog={({
        open,
        onClose,
        onFailure,
        onSuccessfulIntegration
      }: IntegrationSetupDialogProperties) => (
        <SetupDialog
          config={cfg}
          orgId={org.id}
          open={open}
          onClose={onClose}
          onFailure={onFailure}
          onSuccessfulIntegration={onSuccessfulIntegration}
        />
      )}
    />
  );
};
